<template>
  <v-card class="align-stretch" height="100%">

    <v-card-title>
      Products
      <v-spacer></v-spacer>
      <v-btn color="secondary" x-small @click="setCreateProductOpen(true)"><v-icon small>add</v-icon> Add Product</v-btn>
    </v-card-title>

    <v-card-text>

      <v-data-table :loading="productsLoading" :headers="headers" :items="allProducts" hide-default no-data-text="No products" :hide-default-footer="products.length > 10 ? false : true" @click:row="goto">
        <template v-slot:header.cli="{ header }">
          <span class="pr-1">{{ header.text }}</span>
          <BaseInfoTooltip top>The line number</BaseInfoTooltip>
        </template>

        <template v-slot:header.cease_date="{ header }">
          <span class="pr-1">{{ header.text }}</span>
          <BaseInfoTooltip top>The date when the account will be cancelled</BaseInfoTooltip>
        </template>

        <template v-slot:item.id="{ item }">
          <span class="body--text">{{ item.lead_id }}</span>
          <v-icon v-if="item.bundle_product" small class="ml-1">{{ mdiShape }}</v-icon>
        </template>

        <template v-slot:item.product_kind="{ item }"><span class="body--text">{{ kindNameMapping[item.product_kind.toLowerCase()] }}</span></template>
        
        <template v-slot:item.tariff_version="{ item }">{{ voipVersion(item) }}</template>

        <template v-slot:item.cli="{ item }">

          <template v-if="item.product_kind === 'WEB'">
            <a :href="item.service.internal">{{ item.service.internal }}</a>
            <br />
            <a :href="item.service.external">{{ item.service.external }}</a>
          </template>

          <template v-if="item.product_kind === 'VOIP'">
            <span class="body--text">{{item.service.pilot_number}}</span>
            <br />
            <template v-for="(cli,clii) in item.service.clis">
              <span class="body--text" :key="clii">{{ cli }}</span>
            </template>
          </template>

          <template v-if="!(item.product_kind in ['VOIP', 'WEB'])">
            <span class="body--text">{{ item.service.line_number }}</span>
          </template>

        </template>

        <template v-slot:item.status="{ item }"><span class="body--text">{{ item.status }}</span></template>

        <template v-slot:item.live_date="{ item }"><span class="body--text">{{ item.confirmed_date }}</span></template>

        <template v-slot:item.cease_date="{ item }"><span class="body--text">{{ item.ceased_date }}</span></template>

        <template v-slot:item.action="{ item }">

          <LeadLink :leadid="item.id" />

          <v-menu>
            <template v-slot:activator="{ on, attrs }">
              <v-btn small color="secondary" width="50" class="ml-4" v-bind="attrs" v-on="on"><v-icon large>{{mdiDotsHorizontal}}</v-icon></v-btn>
            </template>
            <v-list>

              <v-list-item @click="moveToAnotherAccount(item)">
                <v-list-item-title>MOVE TO ANOTHER ACCOUNT</v-list-item-title>
              </v-list-item>

              <v-list-item @click="moveToNewAccount(item)">
                <v-list-item-title>MOVE TO A NEW ACCOUNT</v-list-item-title>
              </v-list-item>
              
            </v-list>
          </v-menu>
        </template>

      </v-data-table>

    </v-card-text>

    <MoveToAnotherAccountDialog v-model="moveToAnotherAccountOpen" />
    <MoveToNewAccountDialog v-model="moveToNewAccountOpen" />
    
  </v-card>
</template>

<script>
import LeadLink from '@/components/Globals/LeadLink';
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import { mdiShape, mdiDotsHorizontal } from '@mdi/js';
import MoveToAnotherAccountDialog from '@/components/Accounts/MoveToAccount/MoveToAnotherAccountDialog';
import MoveToNewAccountDialog from '@/components/Accounts/MoveToAccount/MoveToNewAccountDialog';

export default {
  components: { LeadLink,MoveToAnotherAccountDialog,MoveToNewAccountDialog },

  data: () => ({
    mdiShape,mdiDotsHorizontal,
    kindSearchUrlMapping: {'web':'sites','voip':'voip','telco':'phonelines'},
    kindNameMapping: {'web':'Website','voip':'VoIP','telco':'Telco'},
    headers: [
      {text: "Product ID",align: "left",sortable: true,value: "id"},
      {text: "Type",align: "left",sortable: false,value: "product_kind"},
      {text: "Version", align: "left", sortable: false, value: "tariff_version"},
      {text: "CLI / URL",align: "left",sortable: true,value: "cli"},
      {text: "Status",align: "left",sortable: true,value: "status"},
      {text: "Live Date",align: "left",sortable: true,value: "live_date"},
      {text: "Cease Date",align: "left",sortable: true,value: "cease_date"},
      {text: "",align: "left",sortable: false,value: "action"}
    ],
    allProducts: [],
    moveToAnotherAccountOpen: false,
    moveToNewAccountOpen: false,
  }),

  computed: {
    ...mapState("products", ["products", 'productsLoading']),
    ...mapGetters("globals", ["hasUserGotRoles"]),
  },

  props: {
    accountId: String,
    height: Number,
  },
  
  async mounted() {
    await this.getAccountProducts(this.accountId);
    this.allProducts = [];
    this.products.forEach(pro => { if(pro.parent_product_id===null){ 
      this.allProducts.push(pro);
    }});
  },

  methods: {
    ...mapActions('alerts', ['setAlert']),
    ...mapActions("products", ["getAccountProducts", "setCreateProductOpen"]),
    ...mapMutations('moveToAccount',['SET_MOVE_TO_ANOTHER_ACCOUNT_PRODUCT','SET_MOVE_TO_NEW_ACCOUNT_PRODUCT']),

    moveToAnotherAccount(product){
      if(!this.hasUserGotRoles(['can-move-product-to-another-account'])){
        this.setAlert({type: 'error', message: `You do not have the permissions to carry out this action`});
      }else if(product.confirmed_date){
        this.setAlert({type: 'error', message: `Moving Live products between accounts is not currently supported`});
      }else if(['Trial','Trial Expired'].includes(product.status)){
        this.setAlert({type: 'error', message: `Moving ${product.status} products between accounts is not currently supported`});
      }else{
        this.SET_MOVE_TO_ANOTHER_ACCOUNT_PRODUCT(product.lead_id);
        this.moveToAnotherAccountOpen = true;
      }
    },

    moveToNewAccount(product){
      if(!this.hasUserGotRoles(['can-move-product-to-another-account'])){
        this.setAlert({type: 'error', message: `You do not have the permissions to carry out this action`});
      }else if(product.confirmed_date){
        this.setAlert({type: 'error', message: `Moving Live products between accounts is not currently supported`});
      }else if(['Trial','Trial Expired'].includes(product.status)){
        this.setAlert({type: 'error', message: `Moving ${product.status} products between accounts is not currently supported`});
      }else{
        this.SET_MOVE_TO_NEW_ACCOUNT_PRODUCT(product.lead_id);
        this.moveToNewAccountOpen = true;
      }
    },

    goto(e){ this.$router.push(`/products/${e.id}/`); },

    getLink(product_kind, lead_id) {
      return `${process.env.VUE_APP_API_HOST}/`+this.kindSearchUrlMapping[product_kind]+'/search?query=' + lead_id;
    },

    voipVersion(item) {
      if (item.product_kind === 'VOIP') {
        const tariffVersions = ['TARIFF_V1', 'TARIFF_V2', 'TARIFF_V3'];

        if (tariffVersions.includes(item.tariff_version)) {
          return item.supplier === 'VOIP3' ? '3' : item.tariff_version === 'TARIFF_V3' ? '2' : '1';
        }
      }
      return '-';
    },
  }
};
</script>

<style lang="scss" scoped>
#product-container {
  padding: 1rem 2rem;
  width: 100%;
}
th,
td {
  padding: 0px 10px !important;
}
.v-data-table ::v-deep tbody tr {
  cursor: pointer !important;
}
</style>
